import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { buildAuthHeaders } from "../../utils/authSession";

function authHeaders() {
  return buildAuthHeaders();
}

const navItems = [
  { href: "/dashboard/admin", label: "Command", icon: "🧠" },
  { href: "/dashboard/admin/users", label: "Users", icon: "👥" },
  { href: "/dashboard/admin/wissenstest", label: "Wissenstest", icon: "🧪" },
  { href: "/dashboard/admin/wissenstest-management", label: "WT Mgmt", icon: "🗂️" },
  { href: "/dashboard/systemstatus", label: "System", icon: "📡" },
];

const emptyQuestion = {
  topic: "general",
  difficulty: "medium",
  type: "single",
  question: "",
  explanation: "",
  options: [
    { answer_text: "", is_correct: true },
    { answer_text: "", is_correct: false },
    { answer_text: "", is_correct: false },
  ],
};

function noCacheParams(params = {}) {
  return { ...params, _ts: Date.now() };
}

const WissenstestAdmin = () => {
  const [q, setQ] = useState("");
  const [questions, setQuestions] = useState([]);
  const [tests, setTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [message, setMessage] = useState("");
  const [questionForm, setQuestionForm] = useState(emptyQuestion);
  const [testForm, setTestForm] = useState({
    name: "Neuer Test",
    description: "",
    topic: "general",
    num_questions: 10,
    time_limit_sec: 900,
    question_ids: [],
  });

  const load = async () => {
    const [qr, tr, sr] = await Promise.all([
      axios.get(`${config.API_BASE_URL}/api/admin/wt/questions`, { params: noCacheParams({ q, page: 1, limit: 50 }), headers: authHeaders() }),
      axios.get(`${config.API_BASE_URL}/api/admin/wt/tests`, { headers: authHeaders(), params: noCacheParams() }),
      axios.get(`${config.API_BASE_URL}/api/admin/wt/stats`, { headers: authHeaders(), params: noCacheParams() }),
    ]);
    setQuestions(qr.data?.rows || []);
    setTests(tr.data?.rows || []);
    setStats(sr.data || null);
  };

  useEffect(() => {
    document.title = "Experts4Europe | Wissenstest Admin";
    load().catch(() => setMessage("Admin-Daten konnten nicht geladen werden."));
  }, []);

  const createQuestion = async () => {
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/wt/questions`, questionForm, { headers: authHeaders() });
      setQuestionForm(emptyQuestion);
      await load();
      setMessage("Frage angelegt.");
    } catch (e) {
      setMessage(e.response?.data?.message || "Frage konnte nicht angelegt werden.");
    }
  };

  const createTest = async () => {
    try {
      await axios.post(`${config.API_BASE_URL}/api/admin/wt/tests`, testForm, { headers: authHeaders() });
      setTestForm((p) => ({ ...p, question_ids: [] }));
      await load();
      setMessage("Test angelegt.");
    } catch (e) {
      setMessage(e.response?.data?.message || "Test konnte nicht angelegt werden.");
    }
  };

  return (
    <CockpitShell
      title="Wissenstest Admin"
      subtitle="Fragenpool, Tests und Basis-Statistiken verwalten."
      badge="Admin Assessment Control"
      navItems={navItems}
    >
      {message ? <p className="rounded-lg border border-cyan-300/30 bg-cyan-500/10 p-3 text-sm text-cyan-100">{message}</p> : null}

      <Tabs defaultValue="questions" className="w-full">
        <TabsList className="bg-white/5">
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="tests">Tests</TabsTrigger>
          <TabsTrigger value="stats">Stats</TabsTrigger>
        </TabsList>

        <TabsContent value="questions">
          <Card className="card-e4e">
            <CardHeader><CardTitle className="font-display text-white">Question Bank</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions" className="border-white/20 bg-slate-950/40 text-white" />
                <Button variant="outline" className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" onClick={() => load()}>
                  Search
                </Button>
              </div>
              <div className="grid gap-2 text-sm text-slate-200">
                {questions.map((item) => (
                  <div key={item.id} className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
                    <p className="text-xs text-slate-400">{item.topic} · {item.difficulty} · {item.type}</p>
                    <p className="font-semibold text-white">{item.question}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="card-e4e">
            <CardHeader><CardTitle className="font-display text-white">Create Question</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input value={questionForm.topic} onChange={(e) => setQuestionForm((p) => ({ ...p, topic: e.target.value }))} placeholder="topic" className="border-white/20 bg-slate-950/40 text-white" />
              <Input value={questionForm.difficulty} onChange={(e) => setQuestionForm((p) => ({ ...p, difficulty: e.target.value }))} placeholder="difficulty easy|medium|hard" className="border-white/20 bg-slate-950/40 text-white" />
              <Input value={questionForm.type} onChange={(e) => setQuestionForm((p) => ({ ...p, type: e.target.value }))} placeholder="type single|multi" className="border-white/20 bg-slate-950/40 text-white" />
              <Textarea value={questionForm.question} onChange={(e) => setQuestionForm((p) => ({ ...p, question: e.target.value }))} placeholder="question" className="border-white/20 bg-slate-950/40 text-white" />
              <Textarea value={questionForm.explanation} onChange={(e) => setQuestionForm((p) => ({ ...p, explanation: e.target.value }))} placeholder="explanation" className="border-white/20 bg-slate-950/40 text-white" />
              {questionForm.options.map((o, idx) => (
                <div key={idx} className="grid gap-2 md:grid-cols-[1fr_auto]">
                  <Input value={o.answer_text} onChange={(e) => {
                    const next = [...questionForm.options];
                    next[idx] = { ...next[idx], answer_text: e.target.value };
                    setQuestionForm((p) => ({ ...p, options: next }));
                  }} placeholder={`Option ${idx + 1}`} className="border-white/20 bg-slate-950/40 text-white" />
                  <label className="flex items-center gap-2 text-sm text-slate-200">
                    <input type="checkbox" checked={!!o.is_correct} onChange={(e) => {
                      const next = [...questionForm.options];
                      next[idx] = { ...next[idx], is_correct: e.target.checked };
                      setQuestionForm((p) => ({ ...p, options: next }));
                    }} />
                    correct
                  </label>
                </div>
              ))}
              <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={createQuestion}>Create Question</Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tests">
          <Card className="card-e4e">
            <CardHeader><CardTitle className="font-display text-white">Create Test</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input value={testForm.name} onChange={(e) => setTestForm((p) => ({ ...p, name: e.target.value }))} placeholder="name" className="border-white/20 bg-slate-950/40 text-white" />
              <Textarea value={testForm.description} onChange={(e) => setTestForm((p) => ({ ...p, description: e.target.value }))} placeholder="description" className="border-white/20 bg-slate-950/40 text-white" />
              <Input value={testForm.topic} onChange={(e) => setTestForm((p) => ({ ...p, topic: e.target.value }))} placeholder="topic" className="border-white/20 bg-slate-950/40 text-white" />
              <Input type="number" value={testForm.num_questions} onChange={(e) => setTestForm((p) => ({ ...p, num_questions: Number(e.target.value || 0) }))} placeholder="num_questions" className="border-white/20 bg-slate-950/40 text-white" />
              <Input type="number" value={testForm.time_limit_sec} onChange={(e) => setTestForm((p) => ({ ...p, time_limit_sec: Number(e.target.value || 0) }))} placeholder="time_limit_sec" className="border-white/20 bg-slate-950/40 text-white" />
              <Textarea
                value={testForm.question_ids.join(",")}
                onChange={(e) => setTestForm((p) => ({ ...p, question_ids: e.target.value.split(",").map((v) => Number(v.trim())).filter((v) => Number.isFinite(v)) }))}
                placeholder="question ids comma-separated"
                className="border-white/20 bg-slate-950/40 text-white"
              />
              <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={createTest}>Create Test</Button>
            </CardContent>
          </Card>
          <Card className="card-e4e">
            <CardHeader><CardTitle className="font-display text-white">Existing Tests</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm text-slate-200">
              {tests.map((t) => (
                <div key={t.id} className="rounded-lg border border-white/10 bg-slate-900/40 p-3">
                  <p className="font-semibold text-white">#{t.id} · {t.name}</p>
                  <p className="text-slate-400">{t.topic} · {t.num_questions} Fragen · {t.time_limit_sec}s</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats">
          <Card className="card-e4e">
            <CardHeader><CardTitle className="font-display text-white">Stats</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm text-slate-200">
              <p>Attempts: {stats?.summary?.attempts || 0}</p>
              <p>Avg Score: {stats?.summary?.avg_score || 0}%</p>
              <p>Min/Max: {stats?.summary?.min_score || 0}% / {stats?.summary?.max_score || 0}%</p>
              {(stats?.by_test || []).map((x) => (
                <div key={x.test_id} className="rounded-lg border border-white/10 bg-slate-900/40 p-2">
                  #{x.test_id} {x.name}: {x.attempts} attempts · avg {x.avg_score}%
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </CockpitShell>
  );
};

export default WissenstestAdmin;
